import firebase from 'firebase/app'
import 'firebase/firestore'
import 'firebase/auth'
import 'firebase/functions'
import 'firebase/storage'
import { Capacitor } from '@capacitor/core'

const firebaseConfig = {
    apiKey: process.env.VUE_APP_FIREBASE_API_KEY,
    authDomain: process.env.VUE_APP_FIREBASE_AUTH_DOMAIN,
    databaseURL: process.env.VUE_APP_FIREBASE_DB_URL,
    projectId: process.env.VUE_APP_FIREBASE_PROJECT_ID,
    storageBucket: process.env.VUE_APP_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.VUE_APP_FIREBASE_MSG_SENDER_ID,
    appId: process.env.VUE_APP_FIREBASE_APP_ID
};

const currentEnv = process.env.VUE_APP_ENV || 'development'

console.warn(`🔥 Firebase Project ID: ${process.env.VUE_APP_FIREBASE_PROJECT_ID} | ENV: ${currentEnv}`)

firebase.initializeApp(firebaseConfig);

if (Capacitor.getPlatform() !== 'ios') {
    firebase.firestore().enablePersistence()
        .catch(function(err) {
            if (err.code == 'failed-precondition') {
                // Multiple tabs open, persistence can only be enabled
                // in one tab at a a time.
                // ...
            } else if (err.code == 'unimplemented') {
                // The current browser does not support all of the
                // features required to enable persistence
                // ...
            }
        });
}

export const db = firebase.firestore()
export const functions = firebase.functions();
export default firebase

export const storage = firebase.storage
export const storageRef = firebase.storage().ref()

export async function deleteStorageFile(savePath) {
    if (!savePath) return
    try {
        await storageRef.child(savePath).delete()
    } catch (err) {
        if (err?.code !== 'storage/object-not-found') console.warn('deleteStorageFile:', err?.code || err)
    }
}

export const { Timestamp, GeoPoint, FieldValue } = firebase.firestore

let authReadyPromise = null

export function waitForAuth() {
    const current = firebase.auth().currentUser
    if (current) return Promise.resolve(current)
    if (!authReadyPromise) {
        authReadyPromise = new Promise(resolve => {
            let settled = false
            let unsubscribe = null
            const finish = user => {
                if (settled) return
                settled = true
                if (unsubscribe) unsubscribe()
                resolve(user || null)
            }
            unsubscribe = firebase.auth().onAuthStateChanged(user => finish(user))
            setTimeout(() => finish(firebase.auth().currentUser), 5000)
        })
    }
    return authReadyPromise
}

const AUTH_ERROR_CODES = ['permission-denied', 'unauthenticated']

export async function withAuthRetry(fn) {
    try {
        return await fn()
    } catch (error) {
        const user = firebase.auth().currentUser
        if (!user || !AUTH_ERROR_CODES.includes(error?.code)) throw error
        await user.getIdToken(true)
        return await fn()
    }
}

export async function getAllByIds(collectionName, ids, chunkSize = 10, concurrency = 20, source = null) {
    if (!ids || !ids.length) return []
    const unique = [...new Set(ids.filter(Boolean))]
    if (!unique.length) return []
    const chunks = []
    for (let i = 0; i < unique.length; i += chunkSize) {
        chunks.push(unique.slice(i, i + chunkSize))
    }
    const docs = []
    for (let i = 0; i < chunks.length; i += concurrency) {
        const batch = chunks.slice(i, i + concurrency)
        const results = await Promise.all(
            batch.map(chunk =>
                db.collection(collectionName)
                    .where(firebase.firestore.FieldPath.documentId(), 'in', chunk)
                    .get(source ? { source } : undefined)
            )
        )
        for (const snap of results) {
            snap.docs.forEach(d => docs.push({ objectID: d.id, id: d.id, ...d.data() }))
        }
    }
    return docs
}
